import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, Outlet } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorBoundary } from "@datadog/browser-rum-react";
import { motion } from "framer-motion";

import useNavigate from "core/hooks/useNavigate";
import Search from "core/components/Field/Search";
import PageHeader from "core/components/PageHeader";
import TableErrorFallback from "core/components/Table/TableErrorFallback";
import { Row } from "core/styles";
import MemberTable from "modules/admin/MemberTable";
import Button from "core/components/Button";
import { ROLES, TABLEAU_ROLES } from "core/constants";
import {
  Card,
  CardBody,
  CardGrid,
  CardHeader,
  CardMetric,
  CardTitle,
  CardSubTitle,
} from "core/components/Card";
import { CardSpacer, CardSubText } from "core/components/Card/Card";
import { json2csv } from "json-2-csv";
import { createColumnHelper } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { DATE_FORMATS } from "core/components/Field/DatePicker";
import { useMembershipListQuery, useOrganizationByIdQuery } from "core/queries";
import useActiveMembership from "core/hooks/useActiveMembership";
import { useMatch } from "react-router";
import { AnimatePresence } from "framer-motion";
import Typography from "core/components/Typography";
import Flex from "core/components/Flex";

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor("name", {
    header: "Name",
    minSize: 225,
  }),
  columnHelper.accessor("email", {
    header: "Email",
    size: 250,
  }),
  columnHelper.accessor("status", {
    header: "Status",
    size: 75,
  }),
  columnHelper.accessor("alternateEmail", {
    header: "Alternate Email",
    size: 200,
  }),
  columnHelper.accessor("role", {
    header: "Role",
    size: 125,
  }),
  columnHelper.accessor("biPortalUser", {
    header: "BI Portal User",
    size: 100,
  }),
  columnHelper.accessor("directDataAccess", {
    header: "Direct Data Access",
    size: 100,
  }),
];

const OrganizationMembersPage = () => {
  const navigate = useNavigate();
  const params = useParams();

  const { orgId: organizationId } = params;
  const { activeMembership } = useActiveMembership();

  const [memberSearchValue, setMemberSearchValue] = useState("");
  const handleSearchChange = (value) => {
    setMemberSearchValue(value);
  };

  const {
    isLoading,
    data: members,
    fetchStatus,
    isError: isMembersError,
    error: membersError,
  } = useMembershipListQuery({
    orgId: organizationId,
    select: (data) => {
      return data.rows.map((member) => {
        member.userId = member.user_id;
        return member;
      });
    },
  });

  const { data: organization } = useOrganizationByIdQuery({
    organizationId,
    include: "licenses",
  });

  const canUpdateMembers =
    activeMembership.role === ROLES.systemAdmin ||
    (activeMembership.role === ROLES.orgAdmin &&
      activeMembership.id === organizationId);

  const memberExportData = useMemo(() => {
    return (members ?? []).map((member) => {
      return {
        name: member.name,
        email: member.email,
        userId: member.userId,
        status: member.active ? "Active" : "Inactive",
        alternateEmail: member.alternateEmail ?? "—",
        role: ROLES[member.role],
        biPortalUser: member.tableauConfig?.tableauUser
          ? TABLEAU_ROLES[member.tableauConfig?.tableauLicense]
          : "—",
        directDataAccess: member.direct_data_access_enabled ? "True" : "False",
      };
    });
  }, [members]);

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Flex style={{ marginBottom: "12px" }}>
        <Typography variant="h2" noMargin style={{ marginBottom: "5px" }}>
          Members
        </Typography>

        <Row
          style={{
            width: "fit-content",
            flex: 1,
            justifyContent: "flex-end",
            alignSelf: "flex-end",
            gap: "15px",
          }}
        >
          <Search value={memberSearchValue} onChange={handleSearchChange} />
          <Button
            variant="outlined"
            width="auto"
            disabled={memberExportData.length === 0}
            onClick={async () => {
              const csv = json2csv(memberExportData, {
                keys: columns.map((column) => {
                  return {
                    field: column.id ?? column.accessorKey,
                    title: column.header,
                  };
                }),
              });
              const blob = new Blob([csv], { type: "text/csv" });
              const element = document.createElement("a");
              element.href = URL.createObjectURL(blob);
              element.download = `${
                organization.name
              }_Members_${DateTime.now().toLocaleString(
                DATE_FORMATS.date
              )}.csv`;
              document.body.appendChild(element);
              element.click();
            }}
          >
            Export
          </Button>
          {canUpdateMembers && (
            <Button
              width="auto"
              onClick={() =>
                navigate(`/organization/${organizationId}/members/add-member`, {
                  state: {
                    from:
                      activeMembership.id === organizationId
                        ? "MyOrganization"
                        : "OrgUsers",
                    orgId: organizationId,
                  },
                })
              }
            >
              Add Member
            </Button>
          )}
        </Row>
      </Flex>

      <ErrorBoundary onError={console.error} fallback={TableErrorFallback}>
        <MemberTable
          data={members || []}
          isRefreshing={fetchStatus === "fetching"}
          isLoading={isLoading}
          canUpdate={canUpdateMembers}
          searchFilter={{
            searchValue: memberSearchValue,
            columns: ["name", "email"],
          }}
          userRole={activeMembership.role}
          style={{
            flexGrow: "1",
          }}
        />
      </ErrorBoundary>

      {/* Render member panel routes */}
      <Outlet />
    </div>
  );
};

export default OrganizationMembersPage;
